/**
 * Maps our cannabis filter and search state onto the HubSpot "Filter Cannabis"
 * event properties.
 *
 * The enum values below mirror the property definitions in the HubSpot portal.
 * If those change, this table has to change with them: HubSpot silently drops a
 * property whose value doesn't match a known option, so anything unmapped is
 * dropped here and logged instead of vanishing without a trace.
 */

import { PurchaseTierFilterType, SortByType } from '../pages/ChooseTreatmentVariants/types';

// trackCustomEvent adds the "pe<portalId>_" prefix HubSpot expects.
export const FILTER_CANNABIS_EVENT = 'filter_cannabis';

export type FilterOption = {
  value: string;
  isSelected: boolean;
  name: string;
  keyWordTitle: string;
};

export type FilterData = { [key: string]: FilterOption[] };

export type FilterCannabisEventInput = {
  filterData: FilterData;
  purchaseTierFilter?: PurchaseTierFilterType | null;
  sortBy?: SortByType;
  searchTerm?: string | null;
};

// filterData key -> HubSpot "Filter Cannabis" property internal name
export const FILTER_KEY_TO_HUBSPOT_PROPERTY: Record<string, string> = {
  thc: 'thc_content',
  cbd: 'cbd_content',
  price: 'price_range',
  genetic: 'genetics',
  effects: 'effects',
  medical_effects: 'medical_effects',
  type: 'product_type',
  // favourite, Delivery Type: no HubSpot property, intentionally omitted
};

// filterData[key][].value (already lower-cased by initFilters) -> HubSpot enum internal
export const FILTER_VALUE_TO_HUBSPOT_ENUM: Record<string, Record<string, string>> = {
  thc: {
    'gte0-lte5': 'thc_low',
    'gte5-lte15': 'thc_medium',
    gte15: 'thc_high',
  },
  cbd: {
    'gte0-lte5': 'cbd_low',
    'gte5-lte15': 'cbd_medium',
    gte15: 'cbd_high',
  },
  price: {
    lte6: 'less_than_6_euro',
    'gte6-lte10': 'from_6_to_10_euro',
    gte10: 'more_than_10_euro',
  },
  genetic: {
    hybrid: 'hybrid',
    indica: 'indica',
    sativa: 'sativa',
  },
  type: {
    flower: 'flowers',
    vape: 'vape',
    edible: 'edibles',
    extract: 'extracts',
  },
  effects: {
    relaxed: 'relaxing',
    focussed: 'focusing',
    euphoric: 'euphoric',
    sociable: 'sociable',
    sleepy: 'sleepy',
    hungry: 'hungry',
    energetic: 'energetic',
    happy: 'happy',
    silly: 'silly',
    // creative, excited: no HubSpot option, dropped
  },
};

// Free-text German labels from product data, so matched lower-cased and trimmed.
// Labels with no HubSpot option yet are simply absent here, and get dropped and logged.
export const MEDICAL_EFFECT_LABEL_TO_HUBSPOT_ENUM: Record<string, string> = {
  alzheimer: 'alzheimer',
  asthma: 'asthma',
  epilepsie: 'epilepsy',
  'hiv/aids': 'hiv_aids',
  krämpfe: 'cramps',
  magersucht: 'anorexia',
  parkinson: 'parkinson',
  appetitlosigkeit: 'loss_of_appetite',
  'chronische schmerzen': 'chronic_pain',
  schlafstörungen: 'sleep_disorders',
  stress: 'stress',
  adhs: 'adhd',
  angstzustände: 'anxiety',
  depression: 'depression',
  kopfschmerzen: 'headache',
  muskeldystrophie: 'muscular_dystrophy',
  erschöpfung: 'exhaustion',
};

export const PURCHASE_TIER_TO_HUBSPOT_ENUM: Record<PurchaseTierFilterType, string> = {
  regular: 'standard',
  premium: 'premium',
};

export const SORT_BY_TO_HUBSPOT_ENUM: Record<string, string> = {
  'price-asc': 'sort_low_price',
  'price-desc': 'sort_high_price',
  'thc-asc': 'sort_low_thc',
  'thc-desc': 'sort_high_thc',
};

const logUnmapped = (kind: string, value: string) => {
  window.clientLogger?.warn('HubSpot Filter Cannabis: unmapped value dropped', {
    code: 'hubspot_filter_cannabis_unmapped_value',
    kind,
    value,
  });
};

/**
 * Builds the event properties from the current filter state. Returns null when the
 * user hasn't actually selected anything, so we never fire an empty event.
 */
export const buildFilterCannabisProperties = ({
  filterData,
  purchaseTierFilter,
  sortBy,
  searchTerm,
}: FilterCannabisEventInput): Record<string, string> | null => {
  const properties: Record<string, string> = {};

  // `service` doesn't count: purchaseTierFilter always has a value (it defaults to
  // premium), so it tells us nothing about whether the user picked anything.
  let hasUserSelection = false;

  for (const [filterKey, options] of Object.entries(filterData || {})) {
    const hubspotProperty = FILTER_KEY_TO_HUBSPOT_PROPERTY[filterKey];
    if (!hubspotProperty) {
      continue; // favourite, Delivery Type, or any other key with no HubSpot property
    }

    const selectedValues = (options || [])
      .filter((option) => option.isSelected)
      .map((option) => {
        if (filterKey === 'medical_effects') {
          const enumValue = MEDICAL_EFFECT_LABEL_TO_HUBSPOT_ENUM[option.value.trim().toLowerCase()];
          if (!enumValue) {
            logUnmapped('medical_effects', option.value);
          }
          return enumValue;
        }

        const enumValue = FILTER_VALUE_TO_HUBSPOT_ENUM[filterKey]?.[option.value];
        if (!enumValue) {
          logUnmapped(filterKey, option.value);
        }
        return enumValue;
      })
      .filter((value): value is string => Boolean(value));

    if (selectedValues.length > 0) {
      properties[hubspotProperty] = selectedValues.join(';');
      hasUserSelection = true;
    }
  }

  if (purchaseTierFilter) {
    const service = PURCHASE_TIER_TO_HUBSPOT_ENUM[purchaseTierFilter];
    if (service) {
      properties.service = service;
    } else {
      logUnmapped('service', purchaseTierFilter);
    }
  }

  if (sortBy) {
    const sortByEnum = SORT_BY_TO_HUBSPOT_ENUM[sortBy];
    if (sortByEnum) {
      properties.sort_by = sortByEnum;
      hasUserSelection = true;
    } else {
      logUnmapped('sort_by', sortBy);
    }
  }

  const trimmedSearchTerm = searchTerm?.trim();
  if (trimmedSearchTerm) {
    properties.search = trimmedSearchTerm;
    hasUserSelection = true;
  }

  return hasUserSelection ? properties : null;
};
